import { useEffect, useState } from 'react';
import {
  TIER_NAMES,
  MVP_SUBSCRIBER_TIERS,
  COMMITMENT_LABELS,
  getCataloguePricing,
  displayPrice,
  fmtUsd,
  termDiscountPercent,
  bestValueTerm,
  type Tier,
  type CommitmentTerm,
} from '../../lib/promo-codes';
import { fetchCatalogue, type Catalogue } from '../../lib/catalogue';
import type { StepProps } from '../OBWWizard';

// Subscribe step — Early Adopter / rack commitment-based pricing.
// (Rebuilt 10 June 2026 per Jeff's verbal pricing instruction; supersedes the
//  earlier flat monthly/annual toggle. See promo-codes.ts for the model + the
//  Business_Reference_v27 conflict note.)
//
//   - Two tiers visible to subscribers (NexaGrowth + NexaSuccess); NexaContext
//     (T3) is post-MVP and excluded (admin actors still see all three).
//   - Commitment selector: Monthly (full rack) / 3 months / 12 months (EA).
//   - ⭐ EVERY NUMBER IS THE CATALOGUE'S (ADR-073 Am.5 §A5.6, 5 October 2026). The prices, the
//     "N% off" on each term and the "Best value · N months free" badge are all derived from
//     `GET /portal/catalogue` — the badge lands on whichever term is cheapest per month, never on a
//     term named in code. Jeff: the 3-month price sits midway between monthly and 12-month,
//     "otherwise no incentive for 12M" — a catalogue `valueByTerm`, not a constant here. Until the
//     catalogue loads the step says so; it never shows a guessed price.
//   - `presentation: 'embedded'` (the public sign-up modal) condenses the step to fit its frame
//     without scrolling (Jeff: "No scrolling"): one row per plan, the recommended one first, the
//     feature lists and the long explainer left to the portal mounts.
//   - Display only — real Stripe processing is LAUNCH (5d-4).

interface PlanDef {
  tier: Tier;
  tag: string;
  recommended?: boolean;
  features: string[];
}

const PLAN_DEFS: Record<Tier, PlanDef> = {
  T1: {
    tier: 'T1',
    tag: 'Current-month Customer Journey Intelligence — no marketing expertise required.',
    recommended: true,
    features: [
      'Monthly Effectiveness Scores (A–F): Discovery, Website, Social',
      'Current-month Growth Effectiveness Report',
      'Availability monitoring with smart email alerts',
      'Fix Guide — prescriptive remediation steps',
      'Loyalty tools',
    ],
  },
  T2: {
    tier: 'T2',
    tag: 'Acquisition & loyalty — 12-month growth narrative with AI interpretation.',
    features: [
      'Everything in NexaGrowth',
      '12 months of historical trend context',
      'Digital breadcrumb attribution across the journey',
      'AI-generated executive insight on what changed',
      'Reach, discovery and effectiveness over time',
    ],
  },
  T3: {
    tier: 'T3',
    tag: 'Contextual intelligence (post-MVP).',
    features: [
      'Everything in NexaSuccess',
      'GEO Scoring (Generative Engine Optimisation)',
      'Contextual Event Attribution',
    ],
  },
};

const COMMITMENT_ORDER: CommitmentTerm[] = ['monthly', 'quarterly', 'annual'];

export function StepSubscribe({ data, onNext, isRequired, actor, nextStepId, presentation }: StepProps) {
  const compact = presentation === 'embedded';
  const initialTier = (data?.selectedTier as Tier) ?? 'T1'; // T1 is default
  const initialCommitment = (data?.commitment as CommitmentTerm) ?? 'annual';

  const [selectedTier, setSelectedTier] = useState<Tier | null>(initialTier);
  const [commitment, setCommitment] = useState<CommitmentTerm>(initialCommitment);
  const [error, setError] = useState('');
  // ADR-085 — displayed tier prices come from the live catalogue endpoint (GET /portal/catalogue,
  // T0 public — works on the unauthenticated sign-up mount). ⛔ NO FALLBACK PRICE: until it resolves
  // the step says it is loading prices; if it fails, it says so and offers a retry. A guessed price
  // is a promise the catalogue does not make (it is how the 3-month term once showed 50% off).
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  const [catalogueTry, setCatalogueTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setCatalogueFailed(false);
    fetchCatalogue()
      .then((cat) => { if (!cancelled) setCatalogue(cat); })
      .catch(() => { if (!cancelled) setCatalogueFailed(true); });
    return () => { cancelled = true; };
  }, [catalogueTry]);

  // T3 (NexaContext) is post-MVP — subscribers see two tiers, admins see all.
  const visibleTiers: Tier[] = actor === 'nexa-admin' ? ['T1', 'T2', 'T3'] : MVP_SUBSCRIBER_TIERS;



  // The badge and each term's "N% off", from the catalogue — for the tier on screen (the selected
  // one; the recommended tier when none is selected yet).
  const badgeTier: Tier = selectedTier ?? 'T1';
  const best = catalogue ? bestValueTerm(catalogue, badgeTier) : null;
  const termPercent = (term: CommitmentTerm): number =>
    catalogue ? termDiscountPercent(getCataloguePricing(catalogue, badgeTier, term)) : 0;
  const maxPercent = Math.max(0, ...COMMITMENT_ORDER.map(termPercent));

  function handleNext() {
    setError('');
    if (!catalogue) { setError('Prices are still loading — please wait a moment.'); return; }
    if (isRequired && !selectedTier) { setError('Please select a plan to continue.'); return; }
    onNext({
      selectedTier,
      commitment,
      // Back-compat: map commitment → legacy billing axis for any older reader.
      billing: commitment === 'monthly' ? 'monthly' : 'annual',
      // D8 — no promo code is collected or sent: the promo capability is not built (Jeff, 5 October
      // 2026), so a code could only preview a discount the server would never bill.
      promoCode: null,
    });
  }

  /** One commitment choice's second line — what the term costs relative to rack, from the data. */
  const termSub = (term: CommitmentTerm): string => {
    const pct = termPercent(term);
    if (term === 'monthly' || pct <= 0) return compact ? 'No lock-in' : 'Full price, no commitment';
    return compact ? `Save ${pct}%` : `Early Adopter price — ${pct}% off, billed upfront`;
  };

  return (
    <div className={compact ? 'obw-step-content obw-subscribe-compact' : 'obw-step-content'}>
      <h2 className="obw-title">Choose your plan</h2>
      <p className="obw-subtitle">
        {compact
          ? 'Commit for longer, pay less each month.'
          : maxPercent > 0
            ? `Nexanomics is priced per domain. Commit to a term to lock the Early Adopter price — up to ${maxPercent}% off the rack rate — for the length of that term. You can change tier later from your account.`
            : 'Nexanomics is priced per domain. You can change tier later from your account.'}
      </p>

      {/* Commitment-term selector */}
      <div className="obw-commit-row" role="radiogroup" aria-label="Commitment term">
        {COMMITMENT_ORDER.map((term) => {
          const isSel = commitment === term;
          return (
            <button
              key={term}
              type="button"
              role="radio"
              aria-checked={isSel}
              className={`obw-commit-option${isSel ? ' selected' : ''}`}
              onClick={() => setCommitment(term)}
            >
              {best?.term === term && (
                <span className="obw-commit-best" data-testid="obw-best-value">
                  Best value{best.monthsFree > 0 ? ` · ${best.monthsFree} months free` : ''}
                </span>
              )}
              <span className="obw-commit-term">{COMMITMENT_LABELS[term]}</span>
              <span className="obw-commit-sub">{termSub(term)}</span>
            </button>
          );
        })}
      </div>

      {!catalogue && (
        catalogueFailed ? (
          <div className="portal-wizard-error" role="alert">
            We couldn’t load our prices just now.{' '}
            <button type="button" className="obw-btn-secondary" onClick={() => setCatalogueTry((n) => n + 1)}>
              Try again
            </button>
          </div>
        ) : (
          <p className="obw-subtitle" role="status" aria-live="polite" data-testid="obw-prices-loading">Loading prices…</p>
        )
      )}

      {catalogue && (
      <div
        className={`obw-plan-grid${selectedTier ? ' has-selection' : ''}${compact ? ' obw-plan-list' : ''}`}
        role="radiogroup"
        aria-label="Plan selection"
      >
        {visibleTiers.map((tier) => {
          const def = PLAN_DEFS[tier];
          // ADR-085 — the live catalogue price for this tier + term. A tier the catalogue does not
          // price (NexaContext, an absent row) is not offered.
          const pricing = getCataloguePricing(catalogue, tier, commitment);
          if (!pricing) {
            // Defined but not priced (NexaContext — `priced: false`): shown to admins as coming soon,
            // never with a made-up price. Not offered in the frame (subscribers never see T3).
            if (compact) return null;
            return (
              <div key={tier} className="obw-plan-card obw-plan-card-unpriced" aria-disabled="true" data-testid={`obw-plan-${tier}`}>
                <div className={`obw-plan-tier ${tier.toLowerCase()}`}>Tier {tier.slice(1)}</div>
                <div className="obw-plan-name">{TIER_NAMES[tier]}</div>
                <div className="obw-plan-tag">{def.tag}</div>
                <p className="obw-plan-billed">Coming soon</p>
              </div>
            );
          }
          // PL24 + the one shared price (lib/promo-codes `displayPrice`) — the payment step shows
          // exactly this figure.
          const { effectiveMonthly, termTotal, showStrike } = displayPrice(pricing, 0);
          const isSelected = selectedTier === tier;
          const showRecommended = def.recommended && actor !== 'nexa-admin';
          const billed = commitment === 'monthly'
            ? `Billed $${fmtUsd(effectiveMonthly)} each month`
            : `Billed $${fmtUsd(termTotal)} once for ${pricing.months} months`;
          if (compact) {
            // One row per plan: name + what it is, and the price for the chosen term.
            return (
              <button
                key={tier}
                type="button"
                className={['obw-plan-row', isSelected ? 'selected' : ''].filter(Boolean).join(' ')}
                onClick={() => setSelectedTier(tier)}
                aria-pressed={isSelected}
                role="radio"
                aria-checked={isSelected}
                data-testid={`obw-plan-${tier}`}
              >
                <span className="obw-plan-row-main">
                  <span className="obw-plan-row-name">
                    {TIER_NAMES[tier]}
                    {showRecommended && <span className="obw-plan-row-rec">Recommended</span>}
                  </span>
                </span>
                {/* Collapsed behind the chosen plan: only the selected row says what it is. */}
                {isSelected && <span className="obw-plan-row-tag">{def.tag}</span>}
                <span className="obw-plan-row-price">
                  <span className="obw-plan-row-amount">
                    {showStrike && <span className="obw-plan-price-strike">${fmtUsd(pricing.rackMonthly)}</span>}
                    ${fmtUsd(effectiveMonthly)}<span className="obw-plan-period">/mo</span>
                  </span>
                </span>
                {isSelected && <span className="obw-plan-row-billed">{billed}</span>}
              </button>
            );
          }
          return (
            <button
              key={tier}
              type="button"
              className={[
                'obw-plan-card',
                showRecommended ? 'recommended' : '',
                isSelected ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => setSelectedTier(tier)}
              aria-pressed={isSelected}
              role="radio"
              aria-checked={isSelected}
            >
              {showRecommended && <span className="obw-plan-rec">Recommended</span>}
              <div className={`obw-plan-tier ${tier.toLowerCase()}`}>Tier {tier.slice(1)}</div>
              <div className="obw-plan-name">{TIER_NAMES[tier]}</div>
              <div className="obw-plan-tag">{def.tag}</div>

              <div className="obw-plan-price">
                {showStrike && (
                  <span className="obw-plan-price-strike">${fmtUsd(pricing.rackMonthly)}</span>
                )}
                <span className={showStrike ? 'obw-plan-price-ea' : ''}>${fmtUsd(effectiveMonthly)}</span>
                <span className="obw-plan-period">/mo</span>
              </div>

              <p className="obw-plan-billed">{billed}</p>
              {pricing.isEa && (
                <p className="obw-plan-revert">Renews at ${fmtUsd(pricing.rackMonthly)}/mo (rack rate)</p>
              )}

              <div className="obw-plan-divider" />
              <ul className="obw-plan-features">
                {def.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
      )}

      {/* D8 (Jeff, 5 October 2026) — the promo-code field is hidden until the promo capability is
          built. Promo codes are a partner/advocate discount that reduces THEIR margin; the old field
          checked a local table and previewed 50% off that the server never billed. The Early
          Adopter term discounts above are catalogue data and unaffected. */}

      {error && <div className="portal-wizard-error" role="alert">{error}</div>}

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={handleNext}
          disabled={(isRequired && !selectedTier) || !catalogue}
        >
          {/* PL22 — reflect the REAL next step. "Continue to payment" only when the
              payment step actually follows; when it was elided (card on file) the
              next step is the snippet/activation, so read plain "Continue". */}
          {nextStepId === 'payment' ? 'Continue to payment' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
